import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["aria-label", "aria-labelledby", "aria-describedby", "children", "className", "color", "onKeyDown", "onRemove", "clientId", "isReadonly", "contentBefore", "omitTooltip", "size"],
  _excluded2 = ["isActive"];
import { lodestarColors } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import { useActive } from "@smartsheet/lodestar-utils";
import { useCombinedThemeMode } from "@smartsheet/sds-theme-internal";
import { CloseIcon } from "@smartsheet/ursa-icons";
import React, { forwardRef } from "react";
import { sizes } from "../../../constants/spacing";
import { useOverflowTooltip } from "../../../util/useOverflowTooltip";
import { Tooltip, TooltipBody } from "../../tooltip";
import { ChipLabel } from "../components/ChipLabel";
import { ChipContainer } from "../styled/ChipContainer";
import { ChipRemoveButton } from "../styled/ChipRemoveButton";
import { chipCustomCssProperties, getTokenForColor } from "../styled/chipConstants";
var elementIsScrollable = function elementIsScrollable(elem) {
  return !!elem && (elem.offsetHeight < elem.scrollHeight || elem.offsetWidth < elem.scrollWidth);
};
export var BaseChip = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _objectSpread2;
  var ariaLabel = _ref["aria-label"],
    ariaLabelledby = _ref["aria-labelledby"],
    ariaDescribedBy = _ref["aria-describedby"],
    children = _ref.children,
    className = _ref.className,
    _ref$color = _ref.color,
    colorVariant = _ref$color === void 0 ? "neutral" : _ref$color,
    onKeyDown = _ref.onKeyDown,
    onRemove = _ref.onRemove,
    clientId = _ref.clientId,
    _ref$isReadonly = _ref.isReadonly,
    isReadonly = _ref$isReadonly === void 0 ? false : _ref$isReadonly,
    contentBefore = _ref.contentBefore,
    _ref$omitTooltip = _ref.omitTooltip,
    omitTooltip = _ref$omitTooltip === void 0 ? false : _ref$omitTooltip,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? "medium" : _ref$size,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useActive = useActive(),
    isActive = _useActive.isActive,
    handlers = _objectWithoutProperties(_useActive, _excluded2);
  var isInteractive = !isReadonly;
  var handleRemove = function handleRemove(event) {
    if (isInteractive) {
      onRemove === null || onRemove === void 0 ? void 0 : onRemove(event);
    }
  };
  var handleKeyDown = function handleKeyDown(event) {
    // allow navigation even if chip is read only
    onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);
    if (isInteractive) {
      switch (event.key) {
        case "Backspace":
        case "Delete":
          handleRemove(event);
      }
    }
  };
  var themeMode = useCombinedThemeMode();
  var buttonProps = onRemove ? {
    role: "button",
    onKeyDown: handleKeyDown,
    tabIndex: 0
  } : {};
  var _useOverflowTooltip = useOverflowTooltip({
      tooltipTargetRef: ref,
      elementIsOverflowing: elementIsScrollable,
      onBlur: rest.onBlur,
      onFocus: rest.onFocus,
      onMouseEnter: rest.onMouseEnter,
      onMouseLeave: rest.onMouseLeave,
      isDisabled: omitTooltip
    }),
    showOverflowTooltip = _useOverflowTooltip.showOverflowTooltip,
    tooltipProps = _useOverflowTooltip.tooltipProps,
    targetProps = _useOverflowTooltip.targetProps,
    elementToMeasureRef = _useOverflowTooltip.elementToMeasureRef;
  var colorProperties = getTokenForColor(colorVariant, themeMode);
  return /*#__PURE__*/React.createElement(ChipContainer, _extends({
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-describedby": ariaDescribedBy,
    "aria-disabled": isReadonly || undefined,
    "data-client-id": clientId,
    className: className,
    isReadonly: isReadonly,
    themeMode: themeMode,
    size: size
  }, buttonProps, rest, targetProps, {
    style: _objectSpread(_objectSpread({}, rest.style), {}, (_objectSpread2 = {}, _defineProperty(_objectSpread2, chipCustomCssProperties.text, "var(--smar--sds--chip--label--color, ".concat(color(colorProperties.text, lodestarColors.neutralDark40), ")")), _defineProperty(_objectSpread2, chipCustomCssProperties.backgroundRest, color(colorProperties.backgroundRest, lodestarColors.buttonDefault)), _defineProperty(_objectSpread2, chipCustomCssProperties.backgroundHover, color(colorProperties.backgroundHover, lodestarColors.buttonDefault)), _defineProperty(_objectSpread2, chipCustomCssProperties.backgroundActive, color(colorProperties.backgroundActive, lodestarColors.buttonDefault)), _objectSpread2))
  }), contentBefore, /*#__PURE__*/React.createElement(ChipLabel, {
    ref: elementToMeasureRef,
    size: size
    // We're going to apply our own here.
    ,
    omitTooltip: true
  }, children), !isReadonly && onRemove ? /*#__PURE__*/React.createElement(ChipRemoveButton, _extends({
    "aria-hidden": true,
    isReadonly: isReadonly
  }, handlers, {
    isActive: isActive,
    onClick: handleRemove,
    themeMode: themeMode,
    size: size
  }), /*#__PURE__*/React.createElement(CloseIcon, {
    size: sizes.medium
  })) : null, showOverflowTooltip && /*#__PURE__*/React.createElement(Tooltip, tooltipProps, /*#__PURE__*/React.createElement(TooltipBody, null, children)));
});
BaseChip.displayName = "BaseChip";
//# sourceMappingURL=BaseChip.js.map